import './CommerceOpsTable.scss';
import cn from 'classnames';
import * as R from 'ramda';
import { ReactElement, useMemo, useState } from 'react';
import { Dropdown, DropdownToggleType } from '@Components/Dropdown';
import { BPMTable, Header } from '@Components/StickyTable/BPMTable';
import { BPMTableWidget } from '@Components/BPMTableWidget';
import { Size } from '@Components/designConstants';
import { DownloadDropdown } from '@Components/DownloadDropdown/DownloadDropdown';
import { FilterBar } from '@Components/FilterBar/FilterBar';
import { ToggleSwitch } from '@Components/ToggleSwitch/ToggleSwitch';
import { DimensionConfig } from './CommerceOpsUtils';

const XLSX_MENU_OPTIONS = ['XLSX'];
const TITLE_AREA_HEIGHT = 36;
// BPMTableWidget chrome around the table: compact header top padding (24) + body top padding (32)
// + bottom padding (32) + 1.5px borders.
const Y_PADDING = 91;
// A LG search row plus the gap under it, when it sits between the header and the table.
const SEARCH_ROW_HEIGHT = 48;

const constructTableHeaders = (config: DimensionConfig<string>): Header[] => {
  const {
    descriptiveFields,
    descriptiveFieldToPrettyName,
    descriptiveFieldToFormatter,
    descriptiveFieldWidth,
    metricsEnum,
    metricToPrettyName,
    metricToFormatter,
    rowFormatter,
    orderBy
  } = config;

  const metricCols = Object.values(metricsEnum).map(field => ({
    label: metricToPrettyName[field],
    name: field,
    flex: 1,
    renderer: row => {
      const formattedVal = (() => {
        if (rowFormatter) {
          return rowFormatter(row, field);
        }
        if (metricToFormatter) {
          return metricToFormatter[field](row[field]);
        }
        return row[field];
      })();
      return (
        <div className={cn('cell-wrapper', { 'is-group-end': row.isGroupEnd })}>
          <div>{formattedVal}</div>
        </div>
      );
    },
    sortAscending: orderBy?.[field]?.ascending,
    sortPriority: orderBy?.[field]?.sortPriority
  }));

  const descriptiveFieldCols = descriptiveFields.map((field, index) => ({
    label: descriptiveFieldToPrettyName[field],
    name: field,
    flex: descriptiveFieldWidth?.[field] ? undefined : 1,
    width: descriptiveFieldWidth?.[field],
    renderer: row => {
      const formatter = descriptiveFieldToFormatter?.[field] ?? ((val: string): string => val);
      const formattedVal = formatter(row[field]);

      return (
        <div className={cn('cell-wrapper', { 'is-group-end': row.isGroupEnd })}>
          <div className='descriptive-field-cell'>{formattedVal}</div>
        </div>
      );
    },
    sortAscending: R.isNil(orderBy) ? true : orderBy?.[field]?.ascending,
    sortPriority: R.isNil(orderBy) ? index : orderBy?.[field]?.sortPriority
  }));

  return [...descriptiveFieldCols, ...metricCols];
};

interface CommerceOpsTableProps<
  K extends string,
  D extends { metrics: Record<string, number>; [key: string]: any }[]
> {
  topRowConfig: {
    showTopRow: boolean;
    showFilterBar?: boolean;
    // For headers too crowded to hold the search on the title's row at narrow widths.
    searchBelowHeader?: boolean;
    dropdownEnabled?: boolean;
  };
  dimensionConfigMap: Record<K, DimensionConfig<string>>;
  dataMap: Record<K, D>;
  initialDimension?: K;
  viewConfig?: { tableRowHeight?: number; componentVhMax?: number | null; fillHeight?: boolean };
  onDownload?: () => void;
  filterFn?: (line: Record<string, any>) => boolean;
}

const renderNoRows = () => <div>No data to show</div>;

const CommerceOpsTable = <
  K extends string,
  D extends { metrics: Record<string, number>; [key: string]: any }[]
>({
  topRowConfig: {
    showTopRow,
    showFilterBar = true,
    searchBelowHeader = false,
    dropdownEnabled = false
  },
  dimensionConfigMap,
  dataMap,
  initialDimension,
  viewConfig: { tableRowHeight = 50, componentVhMax = 40, fillHeight = false } = {},
  onDownload = () => {},
  filterFn = () => true
}: CommerceOpsTableProps<K, D>): ReactElement => {
  const [selectedDimension, setSelectedDimension] = useState<K>(
    initialDimension ??
      (Object.keys(dimensionConfigMap).find(dim => !dimensionConfigMap[dim].disabled) as K)
  );

  const [hiddenTableKpis, setHiddenTableKpis] = useState<Set<string>>(new Set());
  const [tableFilterObj, setTableFilterObj] = useState<{ filterFn: (row: any) => boolean }>();

  const effectiveHiddenKpis = useMemo(
    () =>
      new Set([
        ...Array.from(hiddenTableKpis),
        ...Array.from(dimensionConfigMap[selectedDimension].hiddenGlobalKpis ?? [])
      ]),
    [hiddenTableKpis, dimensionConfigMap, selectedDimension]
  );

  const dropdownOptions = useMemo(() => {
    if (!showTopRow || !dropdownEnabled) {
      return undefined;
    }
    return Object.entries<DimensionConfig<string>>(dimensionConfigMap).map(([name, configObj]) => ({
      value: name as K,
      label: configObj.dimensionPrettyName,
      disabled: configObj.disabled ?? false
    }));
  }, [dimensionConfigMap, dropdownEnabled, showTopRow]);

  const staticTitle = useMemo(() => {
    // static titles should only be used in the scenario that CommerceOpsTables
    // house data from a singular dimension. For multi-dimension data storage,
    // dropdownEnabled should be true
    if (!showTopRow || dropdownEnabled || Object.keys(dimensionConfigMap).length !== 1) {
      return undefined;
    }
    return Object.values<DimensionConfig<string>>(dimensionConfigMap)[0].dimensionPrettyName;
  }, [dimensionConfigMap, dropdownEnabled, showTopRow]);

  const { dimensionSuffix } = dimensionConfigMap[selectedDimension];

  const titleElement = useMemo(() => {
    if (!showTopRow) {
      return undefined;
    }

    if (staticTitle) {
      return <span className='static-title'>{staticTitle}</span>;
    }

    if (R.isNil(dropdownOptions)) {
      return undefined;
    }

    return (
      <Dropdown
        type={DropdownToggleType.WIDGET_TITLE}
        value={selectedDimension}
        options={dropdownOptions}
        onChange={setSelectedDimension}
      />
    );
  }, [showTopRow, staticTitle, dropdownOptions, selectedDimension, setSelectedDimension]);

  const filteredFlattenedTableData = useMemo(() => {
    const nested = dataMap[selectedDimension].filter(
      row =>
        filterFn(row) &&
        (tableFilterObj?.filterFn(row) ?? true) &&
        !effectiveHiddenKpis.has(row.kpi)
    );
    const flattened = nested.map(row => {
      const { metrics, ...descriptiveFields } = row;
      return { ...descriptiveFields, ...metrics };
    });
    const { rowGroupingField } = dimensionConfigMap[selectedDimension];
    if (!rowGroupingField) {
      return flattened;
    }

    for (let i = 0; i < flattened.length; i++) {
      const prev = flattened[i - 1];
      const curr = flattened[i];
      if (R.isNil(prev) || prev[rowGroupingField] === curr[rowGroupingField]) {
        continue;
      }
      flattened[i - 1] = { ...prev, isGroupEnd: true };
    }
    return flattened;
  }, [
    selectedDimension,
    dimensionConfigMap,
    dataMap,
    filterFn,
    tableFilterObj,
    effectiveHiddenKpis
  ]);

  const tableHeaders = useMemo(
    () => constructTableHeaders(dimensionConfigMap[selectedDimension]),
    [selectedDimension, dimensionConfigMap]
  );

  const tableHeight = useMemo(() => {
    // if showTopRow is false, table is presumably
    // a child of larger component setting the height
    if (!showTopRow) {
      return { height: '100%' };
    }

    // The parent section sizes the container, so only the table body scrolls.
    if (fillHeight) {
      return undefined;
    }

    const contentHeight =
      (filteredFlattenedTableData.length + 1) * tableRowHeight +
      TITLE_AREA_HEIGHT +
      Y_PADDING +
      (showFilterBar && searchBelowHeader ? SEARCH_ROW_HEIGHT : 0);
    // A null cap means the container takes its full content height.
    return {
      height:
        componentVhMax === null
          ? `${contentHeight}px`
          : `min(${contentHeight}px, ${componentVhMax}vh)`
    };
  }, [
    filteredFlattenedTableData,
    tableRowHeight,
    componentVhMax,
    fillHeight,
    showTopRow,
    showFilterBar,
    searchBelowHeader
  ]);

  const filterOptions = useMemo(
    () =>
      dimensionConfigMap[selectedDimension].descriptiveFields.map(field => ({
        name: field,
        label: dimensionConfigMap[selectedDimension].descriptiveFieldToPrettyName[field]
      })),
    [dimensionConfigMap, selectedDimension]
  );

  const filterBar = showFilterBar && (
    <FilterBar
      key={selectedDimension}
      size={Size.LG}
      onFilter={filter => setTableFilterObj({ filterFn: filter })}
      lines={dataMap[selectedDimension]}
      options={filterOptions}
      storageKeySuffix={`commerce-ops-${selectedDimension}`}
    />
  );

  const controls = (
    <>
      {!searchBelowHeader && filterBar}
      {dimensionConfigMap[selectedDimension].toggles?.kpis.map(kpi => (
        <ToggleSwitch
          key={kpi.field}
          label={kpi.label}
          checked={!hiddenTableKpis.has(kpi.field)}
          onChange={() =>
            dimensionConfigMap[selectedDimension].toggles?.onKpiToggle(
              kpi,
              hiddenTableKpis,
              setHiddenTableKpis
            )
          }
        />
      ))}
      <DownloadDropdown
        size={Size.LG}
        menuOptions={XLSX_MENU_OPTIONS}
        onClickOptions={[onDownload]}
      />
    </>
  );

  const bpmTable = (
    <BPMTable
      key={selectedDimension}
      className='commerce-ops-table'
      data={filteredFlattenedTableData}
      filterBar={false}
      headers={tableHeaders}
      superHeaderHeight={72}
      noRowsRenderer={renderNoRows}
    />
  );

  if (!showTopRow) {
    return <div style={tableHeight}>{bpmTable}</div>;
  }

  return (
    <div className='commerce-ops-table-container' style={tableHeight}>
      <BPMTableWidget
        header={
          <>
            {titleElement}
            {dimensionSuffix && <span className='dimension-suffix'>{dimensionSuffix}</span>}
          </>
        }
        rightActions={<div className='commerce-ops-header-controls'>{controls}</div>}
      >
        {searchBelowHeader && filterBar}
        {bpmTable}
      </BPMTableWidget>
    </div>
  );
};

export { CommerceOpsTable };
